<template>
  <v-dialog v-model="show" persistent :max-width="width">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        color="primary"
        dark
        outlined
        small
        v-bind="attrs"
        v-on="on"
      >
        Forward Rule
      </v-btn>
    </template>
    <v-card>
      <v-card-title class="title">Add Forwarding Condition</v-card-title>
      <v-card-text>
        <p>Add conditions to forwarding groups based on prompt answers for more granular forwarding.</p>
        <v-row
          align="start"
        >
          <v-col cols="12" md="12">
            <v-select
              v-model="conditionalForwarding.group"
              :items="forwardingGroups(action.forward_groups)"
              item-text="title"
              label="Forward Group"
              hint="Group to apply conditional forwarding rule to."
              return-object
              persistent-hint
            ></v-select>
          </v-col>
          <v-col cols="12" md="12">
            <v-select
              label="Prompt Id"
              v-model="conditionalForwardingScene"
              :items="conditionScenes"
              :return-object="true"
              item-text="id"
              hint="The prompt ID used for this condition"
            ></v-select>
          </v-col>
          <v-col
            class="text-center"
            cols="12"
            md="12"
            v-if="
              conditionalForwardingScene &&
              conditionalForwardingScene.prompt &&
              conditionalForwardingScene.prompt.options
            "
            align-self="center"
          >
            <v-select
              label="Prompt value"
              v-model="conditionalForwarding.value"
              :items="conditionalForwardingScene.prompt.options"
              :multiple="true"
              hint="The value for the prompt ID that will conditionally allow this prompt"
            ></v-select>
          </v-col>
          <v-col
            class="text-center"
            cols="12"
            md="12"
            v-if="conditionalForwardingScene && conditionalForwardingScene.type === 'binary'"
            align-self="center"
          >
            <v-radio-group v-model="conditionalForwarding.value" row>
              <v-radio label="Yes" :value="true"></v-radio>
              <v-radio label="No" :value="false"></v-radio>
            </v-radio-group>
          </v-col>
          <v-col
            class="text-center"
            cols="12"
            md="12"
            v-if="
              conditionalForwardingScene &&
              conditionalForwardingScene.type === 'text' &&
              conditionalForwardingScene.validation &&
              conditionalForwardingScene.validation.expect === 'number'
            "
            align-self="center"
          >
            <v-select
              v-model="conditionalForwarding.value.op"
              :items="operators"
              label="Operator"
            ></v-select>
          </v-col>
          <v-col
            class="text-center"
            cols="12"
            md="12"
            v-if="
              conditionalForwardingScene &&
              conditionalForwardingScene.type === 'text' &&
              conditionalForwardingScene.validation &&
              conditionalForwardingScene.validation.expect === 'number'
            "
            align-self="center"
          >
            <v-text-field
              v-model="conditionalForwarding.value.value"
              label="Value"
              single-line
            ></v-text-field>
          </v-col>
          <v-col 
            cols="12"
            md="12">
            <v-select
              v-model="conditionalForwarding.topic"
              :items="topics"
              item-text="name"
              label="Forward Group Topic"
              hint="Will forward to specific topic, otherwise to General topic"
              @change="addCondition"
              clearable
              return-object
              multiple
              persistent-hint
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="warning" outlined @click="okay()" 
          :disabled="!conditionalForwardingSceneValid">Add</v-btn>
        <v-btn color="grey" outlined @click="cancel()">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  data() {
    return {
      show: false,
      conditionalForwardingScene: {},
      conditionalForwarding: {},
    };
  },
  created() {},
  mounted() {},
  computed: {
    action: {
      get() {
        return this.value;
      },
      set(action) {
        this.value = action;
      },
    },
    topics() {
      let topics = [];

      if (this.conditionalForwarding.group && this.conditionalForwarding.group.topics) {
        Object.keys(this.conditionalForwarding.group.topics).forEach(topicId => {
          topics.push({
            id: topicId,
            name: this.conditionalForwarding.group.topics[topicId]
          })
        })
      }

      return topics;
    },
    conditionalForwardingSceneValid() {
      return this.conditionalForwarding.group
      && (
        (this.conditionalForwarding.value !== undefined && this.conditionalForwarding.value !== null && this.conditionalForwardingScene.id)
        || this.conditionalForwarding.topic
      )
    }
  },
  methods: {
    init() {
      this.conditionalForwardingScene = {};
      this.conditionalForwarding = {
        group: null,
        group_tg_id: null,
        value: null,
        topic: null
      };
    },
    okay() {
      this.addCondition();

      this.$emit("input", this.action);
      this.show = false;
    },
    cancel() {
      this.show = false;
    },
    addCondition() {
      const condition = {
        group_id: this.conditionalForwarding.group.id,
        group_tg_id: this.conditionalForwarding.group.group_tg_id
      };

      if (this.conditionalForwardingScene.id) {
        condition.prompt_id = this.conditionalForwardingScene.id
        condition.value = this.conditionalForwarding.value
      }

      if (this.conditionalForwarding.topic) {
        condition.topic = this.conditionalForwarding.topic
      }

      if (!this.action.forward_group_conditions[this.conditionalForwarding.group.group_tg_id]) {
        this.$set(this.action.forward_group_conditions, this.conditionalForwarding.group.group_tg_id, []);
      }

      let existing = this.action.forward_group_conditions[this.conditionalForwarding.group.group_tg_id].findIndex(fc => fc.prompt_id === condition.prompt_id && fc.value === condition.value)
      if (existing !== -1) this.action.forward_group_conditions[this.conditionalForwarding.group.group_tg_id].splice(existing, 1)

      this.action.forward_group_conditions[this.conditionalForwarding.group.group_tg_id].push(condition)
    },
    forwardingGroups(groups) {
      if (!groups || !groups.length) return [];

      return this.groups.filter((group) => groups.includes(group.group_tg_id));
    },
  },
  watch: {
    show(open) {
      if (open) {
        this.init();
      }
    }
  },
  props: {
    value: {
      type: Object,
      default: () => {
        return {};
      },
    },
    groups: {
      type: Array,
      default: () => {
        return [];
      },
    },
    conditionScenes: {
      type: Array,
      default: () => {
        return [];
      },
    },
    width: {
      type: [Number, String],
      default: 300
    }
  },
};
</script>

<style lang="scss" scoped>
</style>